"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { clientApi } from "@frontend/lib/api";

/**
 * Which side of the workspace you are looking at.
 *
 * Creator = you deliver. Collaborator = you booked the work.
 * Preference is stored in the DB (and mirrored in localStorage for snappy loads).
 */
export type WorkspaceSide = "creator" | "brand";

export const WORKSPACE_SIDE_LABELS: Record<WorkspaceSide, string> = {
  creator: "Creator",
  brand: "Collaborator",
};

const STORAGE_KEY = "sendcollab.workspace-side";

type WorkspaceSideContextValue = {
  side: WorkspaceSide;
  setSide: (next: WorkspaceSide) => void;
  /** True when the signed-in account has collabs (or profiles) on both sides. */
  canToggle: boolean;
};

const WorkspaceSideContext = createContext<WorkspaceSideContextValue | null>(null);

function readStored(): WorkspaceSide {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "creator" || value === "brand") return value;
    if (value === "earning") return "creator";
    if (value === "spending" || value === "spent") return "brand";
  } catch {
    /* private mode */
  }
  return "creator";
}

function writeStored(side: WorkspaceSide) {
  try {
    localStorage.setItem(STORAGE_KEY, side);
  } catch {
    /* private mode */
  }
}

function persistToServer(side: WorkspaceSide) {
  const form = new FormData();
  form.append("side", side);
  void clientApi("/api/settings/workspace-side", { method: "POST", body: form }).catch(() => {
    /* offline / signed out — local cache still applies on this device */
  });
}

/** Persist side without requiring the provider (e.g. before a full-page redirect). */
export function rememberWorkspaceSide(side: WorkspaceSide) {
  writeStored(side);
  persistToServer(side);
}

export function WorkspaceSideProvider({
  children,
  canToggle,
  /** Preferred side when the account only has one — keeps a booker-only view honest. */
  forcedSide = null,
  /** Account preference from /api/viewer — wins over localStorage when present. */
  serverSide = null,
}: {
  children: ReactNode;
  canToggle: boolean;
  forcedSide?: WorkspaceSide | null;
  serverSide?: WorkspaceSide | null;
}) {
  const [side, setSideState] = useState<WorkspaceSide>(() => serverSide ?? readStored());
  const [booted, setBooted] = useState(Boolean(serverSide));

  useEffect(() => {
    if (serverSide) {
      setSideState(serverSide);
      writeStored(serverSide);
      setBooted(true);
      return;
    }
    setSideState(readStored());
    setBooted(true);
  }, [serverSide]);

  const setSide = useCallback((next: WorkspaceSide) => {
    setSideState(next);
    writeStored(next);
    persistToServer(next);
  }, []);

  const resolved: WorkspaceSide = !canToggle && forcedSide ? forcedSide : side;

  const value = useMemo(
    () => ({
      side: booted ? resolved : side,
      setSide,
      canToggle,
    }),
    [booted, resolved, side, setSide, canToggle],
  );

  return (
    <WorkspaceSideContext.Provider value={value}>{children}</WorkspaceSideContext.Provider>
  );
}

export function useWorkspaceSide(): WorkspaceSideContextValue {
  const ctx = useContext(WorkspaceSideContext);
  // Marketing TopNav (and other public shells) render without the provider.
  if (!ctx) {
    return { side: "creator", setSide: () => {}, canToggle: false };
  }
  return ctx;
}

/** Maps the global side onto a collab `yourRole`. */
export function sideToRole(side: WorkspaceSide): "creator" | "brand" {
  return side;
}
